/**
 * Testimonials Section Component
 *
 * React implementation of the testimonials section renderer.
 * Supports variants: carousel, grid, featured, video
 */

import { TestimonialsData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface TestimonialsProps extends SectionProps<TestimonialsData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// HELPER: Star Rating
// =============================================================================

function StarRating({ rating, size = 'md' }: { rating?: number; size?: 'sm' | 'md' | 'lg' }) {
  if (!rating) return null;

  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Testimonials({ data, ctx, sectionId, elements }: TestimonialsProps) {
  const variant = data.variant || 'grid';

  switch (variant) {
    case 'carousel':
      return <TestimonialsCarousel data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'featured':
      return <TestimonialsFeatured data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'video':
      return <TestimonialsVideo data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <TestimonialsGrid data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// GRID VARIANT
// =============================================================================

function TestimonialsGrid({ data, ctx, elements }: TestimonialsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="testimonials"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}

          {/* Aggregate Rating */}
          {data.aggregateRating && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <StarRating rating={data.aggregateRating.value} size="lg" />
              <span className="text-lg font-semibold" style={{ color: recipe.colors.text }}>
                {data.aggregateRating.value.toFixed(1)}
              </span>
              <span style={{ color: recipe.colors.textMuted }}>
                ({data.aggregateRating.count.toLocaleString()} reviews on {data.aggregateRating.platform})
              </span>
            </div>
          )}
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.testimonials?.map((testimonial, i) => {
            const quoteEl = findElement(elements, `testimonials.${i}.quote`);
            const authorEl = findElement(elements, `testimonials.${i}.author`);
            const titleEl = findElement(elements, `testimonials.${i}.title`);
            const avatarEl = findElement(elements, `testimonials.${i}.avatar`);

            return (
              <div
                key={i}
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: recipe.colors.background,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {/* Rating */}
                {testimonial.rating && (
                  <div className="mb-4">
                    <StarRating rating={testimonial.rating} />
                  </div>
                )}

                {/* Quote */}
                {quoteEl ? (
                  <EditableText
                    element={quoteEl}
                    as="blockquote"
                    className="text-lg mb-6"
                    style={{ color: recipe.colors.text }}
                  />
                ) : (
                  <blockquote className="text-lg mb-6" style={{ color: recipe.colors.text }}>
                    "{testimonial.quote}"
                  </blockquote>
                )}

                {/* Author */}
                <div className="flex items-center gap-4">
                  {(testimonial.avatar || avatarEl) && (
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      {avatarEl ? (
                        <EditableImage
                          element={avatarEl}
                          alt={testimonial.author}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.author}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  )}
                  <div>
                    {authorEl ? (
                      <EditableText
                        element={authorEl}
                        as="p"
                        className="font-semibold"
                        style={{ color: recipe.colors.text }}
                      />
                    ) : (
                      <p className="font-semibold" style={{ color: recipe.colors.text }}>
                        {testimonial.author}
                      </p>
                    )}
                    {(testimonial.title || titleEl) && (
                      titleEl ? (
                        <EditableText
                          element={titleEl}
                          as="p"
                          className="text-sm"
                          style={{ color: recipe.colors.textMuted }}
                        />
                      ) : (
                        <p className="text-sm" style={{ color: recipe.colors.textMuted }}>
                          {testimonial.title}
                          {testimonial.company && `, ${testimonial.company}`}
                        </p>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// CAROUSEL VARIANT
// =============================================================================

function TestimonialsCarousel({ data, ctx, elements }: TestimonialsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  // Simple carousel without JS - shows first 3 testimonials
  const displayedTestimonials = data.testimonials?.slice(0, 3) || [];

  return (
    <section
      id="testimonials"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}
        </div>

        {/* Carousel Items */}
        <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory">
          {displayedTestimonials.map((testimonial, i) => {
            const quoteEl = findElement(elements, `testimonials.${i}.quote`);
            const authorEl = findElement(elements, `testimonials.${i}.author`);

            return (
              <div
                key={i}
                className="flex-shrink-0 w-full md:w-1/2 lg:w-1/3 p-8 rounded-2xl snap-center"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                }}
              >
                <StarRating rating={testimonial.rating} />

                {quoteEl ? (
                  <EditableText
                    element={quoteEl}
                    as="blockquote"
                    className="text-xl mt-4 mb-6"
                    style={{ color: recipe.colors.text }}
                  />
                ) : (
                  <blockquote className="text-xl mt-4 mb-6" style={{ color: recipe.colors.text }}>
                    "{testimonial.quote}"
                  </blockquote>
                )}

                <div className="flex items-center gap-3">
                  {testimonial.avatar && (
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.author}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <div>
                    {authorEl ? (
                      <EditableText
                        element={authorEl}
                        as="p"
                        className="font-semibold"
                        style={{ color: recipe.colors.text }}
                      />
                    ) : (
                      <p className="font-semibold" style={{ color: recipe.colors.text }}>
                        {testimonial.author}
                      </p>
                    )}
                    {testimonial.title && (
                      <p className="text-sm" style={{ color: recipe.colors.textMuted }}>
                        {testimonial.title}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// FEATURED VARIANT
// =============================================================================

function TestimonialsFeatured({ data, ctx, elements }: TestimonialsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const featuredQuoteEl = findElement(elements, 'featured.quote');
  const featuredAuthorEl = findElement(elements, 'featured.author');

  const featured = data.featured || data.testimonials?.[0];

  return (
    <section
      id="testimonials"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.primary,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Eyebrow */}
        {(data.eyebrow || eyebrowEl) && (
          <div className="mb-4">
            {eyebrowEl ? (
              <EditableText
                element={eyebrowEl}
                as="span"
                className="text-sm font-semibold uppercase tracking-wider text-white/70"
              />
            ) : (
              <span className="text-sm font-semibold uppercase tracking-wider text-white/70">
                {data.eyebrow}
              </span>
            )}
          </div>
        )}

        {/* Large Quote Icon */}
        <svg
          className="w-16 h-16 mx-auto mb-8 text-white/30"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>

        {/* Featured Quote */}
        {featured && (
          <>
            {featuredQuoteEl ? (
              <EditableText
                element={featuredQuoteEl}
                as="blockquote"
                className="text-2xl md:text-3xl lg:text-4xl font-medium text-white mb-8 leading-relaxed"
                style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
              />
            ) : (
              <blockquote
                className="text-2xl md:text-3xl lg:text-4xl font-medium text-white mb-8 leading-relaxed"
                style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
              >
                "{featured.quote}"
              </blockquote>
            )}

            {/* Rating */}
            {featured.rating && (
              <div className="flex justify-center mb-6">
                <StarRating rating={featured.rating} size="lg" />
              </div>
            )}

            {/* Author */}
            <div className="flex items-center justify-center gap-4">
              {featured.avatar && (
                <img
                  src={featured.avatar}
                  alt={featured.author}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white/30"
                />
              )}
              <div className="text-left">
                {featuredAuthorEl ? (
                  <EditableText
                    element={featuredAuthorEl}
                    as="p"
                    className="text-lg font-semibold text-white"
                  />
                ) : (
                  <p className="text-lg font-semibold text-white">{featured.author}</p>
                )}
                {featured.title && (
                  <p className="text-white/70">{featured.title}</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Aggregate Rating */}
        {data.aggregateRating && (
          <div className="mt-12 pt-8 border-t border-white/20">
            <div className="flex items-center justify-center gap-3">
              {data.aggregateRating.logo && (
                <img
                  src={data.aggregateRating.logo}
                  alt={data.aggregateRating.platform}
                  className="h-8"
                />
              )}
              <span className="text-2xl font-bold text-white">
                {data.aggregateRating.value.toFixed(1)}
              </span>
              <span className="text-white/70">
                ({data.aggregateRating.count.toLocaleString()} reviews)
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// VIDEO VARIANT
// =============================================================================

function TestimonialsVideo({ data, ctx, elements }: TestimonialsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  // Filter testimonials that have video
  const videoTestimonials = data.testimonials?.filter(t => t.videoUrl) || [];
  const textTestimonials = data.testimonials?.filter(t => !t.videoUrl)?.slice(0, 3) || [];

  return (
    <section
      id="testimonials"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Video Testimonials */}
        {videoTestimonials.length > 0 && (
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {videoTestimonials.map((testimonial, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {/* Video */}
                <div className="relative aspect-video bg-gray-900">
                  {testimonial.videoPoster && (
                    <img
                      src={testimonial.videoPoster}
                      alt={`Video testimonial from ${testimonial.author}`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                      style={{ color: recipe.colors.primary }}
                    >
                      <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-6">
                  <p className="font-semibold" style={{ color: recipe.colors.text }}>
                    {testimonial.author}
                  </p>
                  {testimonial.title && (
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>
                      {testimonial.title}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Text Testimonials */}
        {textTestimonials.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6">
            {textTestimonials.map((testimonial, i) => (
              <div
                key={i}
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                }}
              >
                <StarRating rating={testimonial.rating} size="sm" />
                <p className="mt-3 mb-4" style={{ color: recipe.colors.text }}>
                  "{testimonial.quote}"
                </p>
                <p className="font-semibold text-sm" style={{ color: recipe.colors.text }}>
                  {testimonial.author}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default Testimonials;
